import { animated, Spring } from '@react-spring/web';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import Sticker from 'react-stickyfill';
import styled from 'styled-components';
import { Flex, media } from 'ui';
import { announcementBannerHeight } from 'utils/app-configs';
import { DynamicActions, ToggleFilter, ToggleLabel, Toolbar, ToolbarDropdown } from '../../toolbar';

const StyledToolbarDropdown = styled(ToolbarDropdown)`
  width: 220px;
`;

const ToolbarWrapper = styled(Flex)`
  margin: 0 20px;
  height: 55px;
  align-items: center;
`;

const StyledDiv = styled(animated.div)`
  margin: 0 -20px;
  border: ${p => p.bordered && '2px solid #CC0000'} !important;
  background-color: rgb(245, 248, 250) !important;
  position: -webkit-sticky;
  position: sticky;
  top: 161px;
  ${media.tablet`
    top: 55px !important;
    transform: unset !important;
  `};
  z-index: 110;
`;

const TimelineToolbar = ({
  filters,
  statusses,
  objects,
  types,
  selectedFilter,
  selectedStatus,
  selectedObject,
  selectedType,
  handleStatusChange,
  handleFilterChange,
  handleObjectChange,
  handleTypeChange,
  menuVisible,
  hasAnnouncementBanner,
  availableActions,
  reloadTimeline,
}) => {
  const intl = useIntl();
  const translateYpx = -106;
  const updatedTranslateY = hasAnnouncementBanner ?
    `${translateYpx + announcementBannerHeight}px` :
    `${translateYpx}px`;
  const originalTranslateY = hasAnnouncementBanner && window.pageYOffset > 10 ?
    `${announcementBannerHeight}px` :
    '0px';

  return (
    <Sticker>
      <Spring
        to={{
          transform: menuVisible ?
            `translateY(${originalTranslateY})` :
            `translateY(${updatedTranslateY})`,
          boxShadow: menuVisible ? '0px 0px 0px 0px gray' : '0px 5px 20px -5px gray',
          zIndex: menuVisible ? 70 : 110,
        }}
        config={{ duration: 400 }}
      >
        {props =>
          <StyledDiv style={props}>
            <ToolbarWrapper>
              <Toolbar>
                <DynamicActions
                  reload={reloadTimeline}
                  showAsQuickAccess={true}
                  availableActions={availableActions}
                />
                <Toolbar.Item showAsQuickAccess={true}>
                  <ToggleFilter
                    value={selectedStatus}
                    items={statusses}
                    label={intl.formatMessage({
                      id: 'timeline.toolbar.STATUS_LABEL',
                    })}
                    onChange={handleStatusChange}
                    shouldTranslate={true}
                  />
                </Toolbar.Item>
                <Toolbar.Item showAsQuickAccess={true}>
                  <ToggleFilter
                    value={selectedObject}
                    items={objects}
                    label={intl.formatMessage({
                      id: 'timeline.toolbar.OBJECT_LABEL',
                    })}
                    onChange={handleObjectChange}
                    shouldTranslate={true}
                  />
                </Toolbar.Item>
                <Toolbar.Item showAsQuickAccess={true}>
                  <ToggleLabel>
                    {intl.formatMessage({ id: 'timeline.toolbar.TYPE_LABEL' })}
                  </ToggleLabel>
                  <StyledToolbarDropdown
                    value={selectedType}
                    values={types}
                    onChange={handleTypeChange}
                    valueKey='value'
                    valueLabel='text'
                    placeholder={intl.formatMessage({ id: 'common.placeholders.SELECT' })}
                    dropdownMatchSelectWidth={false}
                  />
                </Toolbar.Item>
                <Toolbar.Item showAsQuickAccess={true}>
                  <ToggleFilter
                    value={selectedFilter}
                    items={filters}
                    label={intl.formatMessage({
                      id: 'timeline.toolbar.FILTER_LABEL',
                    })}
                    onChange={handleFilterChange}
                    shouldTranslate={true}
                  />
                </Toolbar.Item>
              </Toolbar>
            </ToolbarWrapper>
          </StyledDiv>}
      </Spring>
    </Sticker>
  );
};

TimelineToolbar.propTypes = {
  filters: PropTypes.array,
  statusses: PropTypes.array,
  objects: PropTypes.array,
  types: PropTypes.array,
  menuVisible: PropTypes.bool,
  hasAnnouncementBanner: PropTypes.bool,
  handleStatusChange: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  handleObjectChange: PropTypes.func.isRequired,
  handleTypeChange: PropTypes.func.isRequired,
  selectedFilter: PropTypes.string.isRequired,
  selectedStatus: PropTypes.string.isRequired,
  selectedObject: PropTypes.string.isRequired,
  selectedType: PropTypes.string.isRequired,
  availableActions: PropTypes.object,
  reloadTimeline: PropTypes.func.isRequired,
};

export default TimelineToolbar;
